<script setup>
import { useAbility } from '@casl/vue'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

import { cookieRef } from '@/@layouts/stores/config'

import RiskService from '@/services/quality/risk.service'
import { RiskLevel, riskLevelColor, riskLevelTitle } from '@/enums/quality/risk-level'
import { RiskStatus, riskStatusColor, riskStatusTitle } from '@/enums/quality/risk-status'
import { RegisterLevel, registerLevelTitle } from '@/enums/quality/register-level'
import { ScoringMethod, scoringMethodTitle } from '@/enums/quality/scoring-method'
import RiskCreateDrawer from '@/views/quality/risk/RiskCreateDrawer.vue'

definePage({
  meta: {
    name: 'app-quality-risk',
    subject: 'risk.menu',
    action: 'register',
  },
})

const route = useRoute()
const router = useRouter()
const { can } = useAbility()
const toast = useToast()

// A query string arrives as the literal string 'true'/'false' (or is simply
// absent) — never a real boolean. The dashboard's heat map and tiles link
// here with these params, so a value that isn't exactly 'true' must resolve
// to false rather than to JS's `Boolean('false') === true` trap.
const queryBool = value => value === 'true' || value === true

// ── Filter options ───────────────────────────────────
const statusOptions = Object.values(RiskStatus).map(value => ({ title: riskStatusTitle(value), value }))
const riskLevelOptions = Object.values(RiskLevel).map(value => ({ title: riskLevelTitle(value), value }))
const registerLevelOptions = Object.values(RegisterLevel).map(value => ({ title: registerLevelTitle(value), value }))

// Risk types — only the currently active ones populate the filter, scoped to
// the selected unit. Unscoped, UnitScope returns types from every unit the
// user belongs to, so the filter offers options that match nothing here.
const selectedUnitId = cookieRef('selectedUnit')

const riskTypes = ref([])

RiskService.listTypes({ active_only: true, unit_id: selectedUnitId.value }, data => {
  riskTypes.value = data?.data ?? data ?? []
})

// Departments & risk owners for the respective filters
const { data: departmentsData } = useApi(createUrl('/masters/departments', {
  query: { showAll: true, select: ['id', 'name'] },
}))

const departments = computed(() => departmentsData.value?.data ?? departmentsData.value ?? [])

const { data: usersData } = useApi(createUrl('/masters/users', {
  query: { showAll: true, select: ['id', 'name'] },
}))

const owners = computed(() => usersData.value?.data ?? usersData.value ?? [])

// ── Filters state ────────────────────────────────────
// Every boolean/scoring_method filter below is seeded from the initial route
// query so a dashboard link (RiskHeatMap's off-map chips, RiskActionTiles'
// tiles) lands already filtered rather than on the unfiltered register with
// a param the page silently ignored on first load.
const riskTypeId = ref(null)
const status = ref(null)
const riskLevel = ref(null)

// register_level arrives as a plain string on a dashboard link (RiskHeatMap's
// goToUnassessed()) — validated against the enum the same way scoringMethod
// is below, so a junk value can't silently filter the register to nothing.
const validRegisterLevels = Object.values(RegisterLevel)

const registerLevel = ref(validRegisterLevels.includes(route.query.register_level) ? route.query.register_level : null)

// department_id is a numeric id but arrives as a string in the query string —
// cast it so it matches AppSelect's numeric item-value and the equality
// filter actually applies.
const departmentId = ref(route.query.department_id ? Number(route.query.department_id) : null)

const riskOwnerId = ref(null)

// unit_id: RiskHeatMap's goToUnassessed() sends the heat map's own unit
// (RiskService::query() now accepts it) so the register reproduces the same
// count instead of UnitScope's every-unit-the-user-belongs-to span. No UI
// control for this — it only ever arrives via a dashboard link.
const unitId = ref(route.query.unit_id ? Number(route.query.unit_id) : null)

// 'overdue_review' is the name the dashboard's links use (mirroring the
// 'reviews_overdue' tile/queue key); 'overdue' is this switch's own
// pre-existing param name for the identical backend scope — see
// RiskService::query(). Both are honoured on load.
const overdue = ref(queryBool(route.query.overdue_review) || queryBool(route.query.overdue))
const expiredAcceptance = ref(queryBool(route.query.expired_acceptance))
const unassessed = ref(queryBool(route.query.unassessed))

const validScoringMethods = Object.values(ScoringMethod)

const scoringMethod = ref(validScoringMethods.includes(route.query.scoring_method) ? route.query.scoring_method : null)

const scoringMethodOptions = validScoringMethods.map(value => ({ title: scoringMethodTitle(value), value }))

// `searchInput` is what the field holds, `searchQuery` is what the request
// uses — the field needs a real v-model so "Clear all" can empty it. While it
// was write-only (an @input handler with no binding) nothing could reset it,
// which left the user staring at an empty table after clearing every filter.
const searchInput = ref('')
const searchQuery = ref('')

// The unit filter has no control of its own — it only ever arrives on a
// dashboard link (see the note above). Without a visible marker the register
// silently shows one unit's risks with nothing on screen explaining why, so it
// is surfaced as a removable chip alongside the other active filters.
const { data: unitsData } = useApi(createUrl('/masters/units', {
  query: { showAll: true, select: ['id', 'name'] },
}))

const unitName = computed(() => {
  if (!unitId.value) return null

  const units = unitsData.value?.data ?? unitsData.value ?? []

  return units.find(unit => unit.id === unitId.value)?.name ?? `Unit #${unitId.value}`
})

// Every filter the user can currently have applied, paired with how to clear
// it. Drives both the active count and the individual chips.
const activeFilters = computed(() => [
  { key: 'riskType', label: 'Type', value: riskTypes.value.find(t => t.id === riskTypeId.value)?.name, clear: () => { riskTypeId.value = null } },
  { key: 'status', label: 'Status', value: status.value ? riskStatusTitle(status.value) : null, clear: () => { status.value = null } },
  { key: 'riskLevel', label: 'Level', value: riskLevel.value ? riskLevelTitle(riskLevel.value) : null, clear: () => { riskLevel.value = null } },
  { key: 'registerLevel', label: 'Register', value: registerLevel.value ? registerLevelTitle(registerLevel.value) : null, clear: () => { registerLevel.value = null } },
  { key: 'department', label: 'Department', value: departments.value.find(d => d.id === departmentId.value)?.name, clear: () => { departmentId.value = null } },
  { key: 'owner', label: 'Owner', value: owners.value.find(o => o.id === riskOwnerId.value)?.name, clear: () => { riskOwnerId.value = null } },
  { key: 'scoringMethod', label: 'Scoring', value: scoringMethod.value ? scoringMethodTitle(scoringMethod.value) : null, clear: () => { scoringMethod.value = null } },
  { key: 'unit', label: 'Unit', value: unitName.value, clear: () => { unitId.value = null } },
  { key: 'overdue', label: 'Overdue', value: overdue.value ? 'Yes' : null, clear: () => { overdue.value = false } },
  { key: 'expiredAcceptance', label: 'Expired acceptance', value: expiredAcceptance.value ? 'Yes' : null, clear: () => { expiredAcceptance.value = false } },
  { key: 'unassessed', label: 'Unassessed', value: unassessed.value ? 'Yes' : null, clear: () => { unassessed.value = false } },
].filter(filter => filter.value))

const clearAllFilters = () => {
  activeFilters.value.forEach(filter => filter.clear())
  searchInput.value = ''
}

// True when anything at all is narrowing the register — drives the empty state's
// wording and its clear button, which must also appear when only search is set.
const hasAnyNarrowing = computed(() => activeFilters.value.length > 0 || !!searchQuery.value)

// Only the search term is set here. Resetting `page` as well used to fire the
// request twice from any page but the first: this callback reset page 3 -> 1
// synchronously, so the `filters` watcher below then saw page already at 1 and
// fetched, while the `[page]` watcher fetched again on the same 3 -> 1 change.
// The `filters` watcher owns the reset for every other filter — search is no
// different.
watch(searchInput, useDebounceFn(val => {
  searchQuery.value = val
}, 500))

const filters = computed(() => ({
  riskTypeId: riskTypeId.value,
  status: status.value,
  riskLevel: riskLevel.value,
  registerLevel: registerLevel.value,
  departmentId: departmentId.value,
  riskOwnerId: riskOwnerId.value,
  unitId: unitId.value,
  overdue: overdue.value,
  expiredAcceptance: expiredAcceptance.value,
  unassessed: unassessed.value,
  scoringMethod: scoringMethod.value,
  search: searchQuery.value,
}))

// ── Table state ──────────────────────────────────────
const itemsPerPage = ref(10)
const page = ref(1)

// NOTE: the risks list endpoint does not accept sort params (see backend
// facts in the task brief), so every column is non-sortable rather than
// exposing sort controls that would silently do nothing.
const headers = [
  { title: 'Code', key: 'code', sortable: false },
  { title: 'Title', key: 'title', sortable: false },
  { title: 'Type', key: 'risk_type.name', sortable: false },
  { title: 'Inherent', key: 'inherent_risk_level', sortable: false },
  { title: 'Residual', key: 'residual_risk_level', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Owner', key: 'owner.name', sortable: false },
  { title: 'Next Review', key: 'next_review_due', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false, width: 100 },
]

const risks = ref([])
const totalRisks = ref(0)
const isFetching = ref(false)

const buildQuery = () => ({
  risk_type_id: riskTypeId.value || undefined,
  status: status.value || undefined,
  risk_level: riskLevel.value || undefined,
  register_level: registerLevel.value || undefined,
  department_id: departmentId.value || undefined,
  risk_owner_id: riskOwnerId.value || undefined,
  unit_id: unitId.value || undefined,
  overdue: overdue.value || undefined,
  expired_acceptance: expiredAcceptance.value || undefined,
  unassessed: unassessed.value || undefined,
  scoring_method: scoringMethod.value || undefined,
  search: searchQuery.value || undefined,
  per_page: itemsPerPage.value,
  page: page.value,
})

// Monotonic token guarding against out-of-order responses: filter changes,
// search and pagination each fire an independent request with no
// cancellation, so a slow earlier response could otherwise land after a
// faster later one and silently overwrite correct rows with stale ones.
let fetchToken = 0

const fetchRisks = async () => {
  const token = ++fetchToken

  isFetching.value = true
  await RiskService.list(buildQuery(), data => {
    // Ignore a response a newer request has already superseded.
    if (token !== fetchToken) return
    risks.value = data?.data ?? []
    totalRisks.value = data?.meta?.total ?? 0
  })
  if (token === fetchToken) isFetching.value = false
}

// Any filter change (including debounced search) resets to page 1. If we're
// already on page 1 that reset is a no-op, so fetch directly; otherwise let
// the page watcher below fire the fetch once the reset lands.
watch(filters, () => {
  if (page.value === 1)
    fetchRisks()
  else
    page.value = 1
})

watch([page, itemsPerPage], fetchRisks)

fetchRisks()

// ── Row navigation ───────────────────────────────────
// unplugin-vue-router derives route names from the file path, so
// src/pages/app/quality/risk/[id].vue is named `app-quality-risk-id` — see
// the same convention at src/views/quality/infection-control/exposure/ExposureTable.vue:16.
const goToView = item => {
  router.push({ name: 'app-quality-risk-id', params: { id: item.id } })
}

// ── Export ───────────────────────────────────────────
// RiskController::export queues RiskRegisterExportJob and returns only
// { export_id } — the XLSX itself arrives asynchronously over the
// ExportStatusEvent broadcast, which ExportSnackbar (mounted globally in
// src/layouts/components/DefaultLayoutWithVerticalNav.vue) already listens for:
// it renders the progress bar and triggers the download on 'completed'. There
// is deliberately no polling here.
//
// The job builds its query through RiskService::query(), the same path the list
// endpoint uses, so the export honours exactly the filters on screen. Pagination
// is dropped — an export is the whole filtered register.
const isExporting = ref(false)

const exportRegister = async () => {
  const exportQuery = buildQuery()

  delete exportQuery.per_page
  delete exportQuery.page

  isExporting.value = true
  await RiskService.exportRegister(exportQuery, () => {
    toast.success('Export started. The file will download when it is ready.', 'success')
  })
  isExporting.value = false
}

// ── Add Risk ─────────────────────────────────────────
const isCreateDrawerVisible = ref(false)

const onRiskSaved = () => {
  fetchRisks()
}
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText class="d-flex flex-wrap gap-4 align-center">
        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 12rem;"
        >
          <AppSelect
            v-model="riskTypeId"
            :items="riskTypes"
            item-title="name"
            item-value="id"
            label="Risk Type"
            placeholder="All types"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 10rem;"
        >
          <AppSelect
            v-model="status"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            label="Status"
            placeholder="All statuses"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 10rem;"
        >
          <AppSelect
            v-model="riskLevel"
            :items="riskLevelOptions"
            item-title="title"
            item-value="value"
            label="Risk Level"
            placeholder="All levels"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 10rem;"
        >
          <AppSelect
            v-model="registerLevel"
            :items="registerLevelOptions"
            item-title="title"
            item-value="value"
            label="Register Level"
            placeholder="All register levels"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 12rem;"
        >
          <AppSelect
            v-model="departmentId"
            :items="departments"
            item-title="name"
            item-value="id"
            label="Department"
            placeholder="All departments"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 12rem;"
        >
          <AppSelect
            v-model="riskOwnerId"
            :items="owners"
            item-title="name"
            item-value="id"
            label="Risk Owner"
            placeholder="All owners"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 10rem;"
        >
          <AppSelect
            v-model="scoringMethod"
            :items="scoringMethodOptions"
            item-title="title"
            item-value="value"
            label="Scoring Method"
            placeholder="All methods"
            density="compact"
            clearable
          />
        </div>

        <VSwitch
          v-model="overdue"
          label="Overdue"
          color="error"
          density="compact"
          hide-details
        />

        <VSwitch
          v-model="expiredAcceptance"
          label="Expired Acceptance"
          color="error"
          density="compact"
          hide-details
        />

        <VSwitch
          v-model="unassessed"
          label="Unassessed"
          color="secondary"
          density="compact"
          hide-details
        />
      </VCardText>

      <!--
        Active filters, including `unit` which has no control of its own and
        otherwise scopes the register invisibly on a dashboard link.
      -->
      <template v-if="activeFilters.length">
        <VDivider />
        <VCardText class="d-flex flex-wrap align-center gap-2 py-3">
          <span class="text-body-2 text-medium-emphasis me-1">
            {{ activeFilters.length }} {{ activeFilters.length === 1 ? 'filter' : 'filters' }} applied
          </span>

          <VChip
            v-for="filter in activeFilters"
            :key="filter.key"
            size="small"
            closable
            label
            @click:close="filter.clear()"
          >
            {{ filter.label }}: {{ filter.value }}
          </VChip>

          <VBtn
            variant="text"
            size="small"
            color="primary"
            @click="clearAllFilters"
          >
            Clear all
          </VBtn>
        </VCardText>
      </template>
    </VCard>

    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <AppTextField
            v-model="searchInput"
            placeholder="Search..."
            density="compact"
            clearable
            style="inline-size: 280px;"
          >
            <template #prepend-inner>
              <VIcon
                icon="tabler-search"
                size="16"
              />
            </template>
          </AppTextField>
        </template>
        <template #append>
          <div class="d-flex align-center gap-3">
            <VBtn
              v-if="can('create', 'quality.risk')"
              color="primary"
              variant="flat"
              prepend-icon="tabler-plus"
              @click="isCreateDrawerVisible = true"
            >
              Add Risk
            </VBtn>

            <VBtn
              v-if="can('export', 'quality.risk')"
              color="secondary"
              variant="outlined"
              prepend-icon="tabler-file-export"
              :loading="isExporting"
              @click="exportRegister"
            >
              Export
            </VBtn>

            <IconBtn
              variant="tonal"
              class="rounded"
              :loading="isFetching"
              @click="fetchRisks"
            >
              <VIcon icon="tabler-refresh" />
              <VTooltip activator="parent">
                Refresh
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VCardItem>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="risks"
        :items-length="totalRisks"
        :loading="isFetching"
        class="text-no-wrap"
        hover
        @click:row="(_, { item }) => goToView(item)"
      >
        <!-- Type -->
        <template #item.risk_type.name="{ item }">
          {{ item.risk_type?.name ?? '—' }}
        </template>

        <!-- Inherent -->
        <template #item.inherent_risk_level="{ item }">
          <VChip
            v-if="item.inherent_risk_level"
            :color="riskLevelColor(item.inherent_risk_level)"
            size="small"
            label
          >
            {{ item.scoring_method === 'RPN_FMEA' ? `RPN ${item.inherent_rpn}` : item.inherent_score }} · {{ riskLevelTitle(item.inherent_risk_level) }}
          </VChip>
          <span
            v-else
            class="text-disabled"
          >—</span>
        </template>

        <!-- Residual -->
        <template #item.residual_risk_level="{ item }">
          <VChip
            v-if="item.residual_risk_level"
            :color="riskLevelColor(item.residual_risk_level)"
            size="small"
            label
          >
            {{ item.scoring_method === 'RPN_FMEA' ? `RPN ${item.residual_rpn}` : item.residual_score }} · {{ riskLevelTitle(item.residual_risk_level) }}
          </VChip>
          <span
            v-else
            class="text-disabled"
          >—</span>
        </template>

        <!-- Status -->
        <template #item.status="{ item }">
          <VChip
            :color="riskStatusColor(item.status)"
            size="small"
            label
          >
            {{ riskStatusTitle(item.status) }}
          </VChip>
        </template>

        <!-- Owner -->
        <template #item.owner.name="{ item }">
          {{ item.owner?.name ?? '—' }}
        </template>

        <!-- Next Review -->
        <template #item.next_review_due="{ item }">
          <div class="d-flex align-center gap-1">
            <VIcon
              v-if="item.is_review_overdue"
              icon="tabler-alert-triangle"
              color="error"
              size="16"
            />
            <span>{{ item.next_review_due ? formatDate(item.next_review_due) : '—' }}</span>
          </div>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex justify-start gap-1">
            <IconBtn
              variant="tonal"
              class="rounded"
              size="small"
              @click.stop="goToView(item)"
            >
              <VIcon icon="tabler-eye" />
              <VTooltip activator="parent">
                View
              </VTooltip>
            </IconBtn>
          </div>
        </template>

        <!--
          Empty state. Without this the table falls back to Vuetify's bare
          "No data available", which on a filtered-to-nothing register gives no
          hint that filters are the reason and no way back.
        -->
        <template #no-data>
          <div class="d-flex flex-column align-center text-center py-12">
            <VIcon
              icon="tabler-shield-search"
              size="48"
              class="mb-4 text-disabled"
            />
            <h6 class="text-h6 mb-2">
              {{ hasAnyNarrowing ? 'No risks match your search and filters' : 'No risks on the register yet' }}
            </h6>
            <p class="text-body-2 text-medium-emphasis mb-4">
              {{ hasAnyNarrowing
                ? 'Try widening them, or clear everything to see the whole register.'
                : 'Risks you add will be listed here.' }}
            </p>
            <VBtn
              v-if="hasAnyNarrowing"
              variant="tonal"
              size="small"
              @click="clearAllFilters"
            >
              Clear search and filters
            </VBtn>
          </div>
        </template>

        <!-- Bottom -->
        <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
            <div class="d-flex align-center gap-3">
              <p class="text-sm text-medium-emphasis mb-0">
                {{ totalRisks }} total records
              </p>

              <!--
                Rows per page. `itemsPerPage` already had a v-model and a
                watcher, but this slot overrides Vuetify's whole footer, so
                nothing ever rendered a control for it and the register was
                pinned to 10.
              -->
              <AppSelect
                v-model="itemsPerPage"
                :items="[10, 25, 50, 100]"
                density="compact"
                variant="plain"
                hide-details
                style="inline-size: 5rem;"
              />
            </div>
            <VPagination
              v-model="page"
              :length="Math.ceil(totalRisks / itemsPerPage)"
              :total-visible="5"
            />
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <RiskCreateDrawer
      v-model:is-drawer-open="isCreateDrawerVisible"
      :risk="null"
      @saved="onRiskSaved"
    />
  </section>
</template>
